import type { IRoleObj } from '../../../../../redux/slices/groupRolesListSlice';
import type { MemberRoleKey } from './types';

/**
 * Keeping the role list (redux groupRoleList, from GET /groupsListForUser) in
 * step with the learner's board (GET /members/learning-framework/{orgId}).
 *
 * Both read the same membership on the server, but the list is read once at
 * sign-in and cached for five minutes, while the board is read fresh every
 * time it opens. A program the learner joined in between is on the board and
 * not in the list: auto-enrolment when onboarding saved their member
 * attributes (the case of a learner invited to the org with no program), or
 * an invite finished while they were signed in.
 *
 * The list is what every way into a program looks the program up in, and what
 * `lastseen` is written from, so a program it lacks used to open the lesson
 * page with no program (a blank page), and the Header sends the whole session
 * back to the start when the selected program is not in the list. The board
 * is the fresher reading of the same membership, so the list takes the
 * board's programs: at once, from the board's own data, and again from the
 * server. No React here, so it can be tested on its own.
 */

/** What the list needs to know about a program on the board: the card's own data is enough. */
export interface BoardProgram {
  id: string;
  name: string;
  /** Empty for a masterclass, which has no roles: the learner is taken as a learner there. */
  roles: MemberRoleKey[];
}

const sameEntry = (a: Pick<IRoleObj, 'id' | 'role'>, b: Pick<IRoleObj, 'id' | 'role'>) =>
  a.id === b.id && a.role === b.role;

/** The roles a program on the board gives the learner, as the list names them. */
const rolesOf = (program: BoardProgram): string[] => (program.roles.length > 0 ? program.roles : ['mentee']);

/** The list's entry for a program and role, as the server would build it. */
export const entryFor = (program: Pick<BoardProgram, 'id' | 'name'>, role: string): IRoleObj => ({
  id: program.id,
  name: program.name,
  role,
  active: true
});

/** The entries the list lacks for the programs on the board, one per program and role, in board order. */
export const missingRoleListEntries = (entries: IRoleObj[], programs: BoardProgram[]): IRoleObj[] => {
  const missing: IRoleObj[] = [];
  programs.forEach((program) => {
    rolesOf(program).forEach((role) => {
      const entry = entryFor(program, role);
      if (!entries.some((e) => e && sameEntry(e, entry)) && !missing.some((e) => sameEntry(e, entry))) {
        missing.push(entry);
      }
    });
  });
  return missing;
};

/** The list with these entries after its own, leaving out any it already has. The same list when nothing is added. */
export const withEntries = (entries: IRoleObj[], added: IRoleObj[]): IRoleObj[] => {
  const fresh = added.filter((entry) => !entries.some((e) => e && sameEntry(e, entry)));
  return fresh.length > 0 ? [...entries, ...fresh] : entries;
};

/**
 * The entry to select when a program is opened from the board: the list's
 * entry for the role wanted, else the list's entry for the program under any
 * role, else one built from the card (`known` false), which the caller adds
 * to the list so every lookup in the app agrees with the selection.
 */
export const entryForProgram = (
  entries: IRoleObj[],
  program: Pick<BoardProgram, 'id' | 'name'>,
  role: string
): { entry: IRoleObj; known: boolean } => {
  const known =
    entries.find((e) => e?.id === program.id && e?.role === role) || entries.find((e) => e?.id === program.id);
  return known ? { entry: known, known: true } : { entry: entryFor(program, role), known: false };
};
